import { Globe, Linkedin, Mail, MapPin } from 'lucide-react'
import React from 'react'
import { Card, Heading, Link, Text } from '../ui'

/**
 * Props interface for the ContactSection component
 */
export interface ContactSectionProps {
  /** Unique identifier for the section */
  id?: string
  /** CSS class name for custom styling */
  className?: string
}

/**
 * Contact section component displaying contact information and availability.
 *
 * Features:
 * - Professional contact details
 * - Availability and work status information
 * - Accessible links with proper ARIA labels
 * - Icons for visual recognition
 * - Responsive grid layout
 */
export const ContactSection: React.FC<ContactSectionProps> = ({
  id,
  className = '',
}) => {
  return (
    <section
      className={`about-section ${className}`}
      aria-labelledby="contact-heading"
      id={id}
    >
      <Heading
        as="h2"
        size="xl"
        variant="default"
        className="section-title"
        id="contact-heading"
      >
        Let's Connect
      </Heading>

      <Card variant="surface" padding="lg" className="contact-card">
        <div className="contact-content">
          <div className="contact-text">
            <Text size="base" variant="primary" className="contact-intro">
              I'm currently employed full-time at The Regis Company, but I'm
              always interested in connecting with fellow technologists,
              discussing architecture challenges, or exploring consulting
              opportunities that align with my expertise and values.
            </Text>

            <Text size="base" variant="secondary" className="contact-note">
              Whether you're facing scaling challenges, considering an AI
              integration, or just want to discuss software architecture, I'd
              love to hear from you. The best problems are solved through
              collaboration and diverse perspectives.
            </Text>
          </div>

          <div
            className="contact-links"
            role="list"
            aria-label="Contact information"
          >
            <div className="contact-item" role="listitem">
              <Mail size={20} aria-hidden="true" />
              <Link
                href="mailto:lucas@lucasstufflebeam.com"
                variant="default"
                aria-label="Send email to Lucas Stufflebeam"
              >
                lucas@lucasstufflebeam.com
              </Link>
            </div>

            <div className="contact-item" role="listitem">
              <Linkedin size={20} aria-hidden="true" />
              <Link
                href="https://linkedin.com/in/lstufflebeam"
                target="_blank"
                rel="noopener noreferrer"
                variant="default"
                aria-label="View Lucas Stufflebeam's LinkedIn profile (opens in new tab)"
              >
                LinkedIn Profile
              </Link>
            </div>

            <div className="contact-item" role="listitem">
              <Globe size={20} aria-hidden="true" />
              <Link
                href="https://lucasstufflebeam.com"
                target="_blank"
                rel="noopener noreferrer"
                variant="default"
                aria-label="Visit Lucas Stufflebeam's portfolio website (opens in new tab)"
              >
                Portfolio Website
              </Link>
            </div>

            <div className="contact-item" role="listitem">
              <MapPin size={20} aria-hidden="true" />
              <Text
                variant="primary"
                aria-label="Location: Denver, Colorado, Remote work available"
              >
                Denver, CO • Remote
              </Text>
            </div>
          </div>
        </div>
      </Card>
    </section>
  )
}
